import { useEffect, useRef, useState } from "react";
import { LeadAPI } from "api/lead/service";
import {
  Plus,
  Search,
  Phone,
  MapPin,
  TrendingUp,
  Calendar,
  IndianRupee,
  Users,
  Building2,
  Clock,
  Ruler,
  FileText,
  X,
  ChevronDown,
  Activity,
  Check,
} from "lucide-react";
import { formatCurrency } from "utils/utilityFunctions";
import LoadingScreen from "components/UI_components/LoadingScreen";

function parseBudgetRangeToBudgetAmount(range) {
  if (!range) return null;
  const s = String(range).trim();
  // "50+" => 50 Lakhs
  if (s.endsWith("+")) {
    const n = Number(s.replace("+", ""));
    return Number.isFinite(n) ? n * 100000 : null;
  }
  // "5-10" => average of range (in Lakhs)
  const parts = s.split("-").map((x) => Number(x));
  if (
    parts.length === 2 &&
    Number.isFinite(parts[0]) &&
    Number.isFinite(parts[1])
  ) {
    return ((parts[0] + parts[1]) / 2) * 100000;
  }
  if (parts.length >= 1 && Number.isFinite(parts[0])) return parts[0] * 100000;
  return null;
}

function parseBudgetToNumber(budget) {
  if (budget == null) return undefined;
  if (typeof budget === "number")
    return Number.isFinite(budget) ? budget : undefined;
  const n = Number(String(budget).replace(/,/g, ""));
  return Number.isFinite(n) ? n : undefined;
}

function normalizeLeadToClient(lead) {
  if (!lead) return null;
  const createdRaw = lead.created_on || lead.createdAt || lead.created_at || "";
  return {
    id: String(lead.id ?? lead.pk ?? ""),
    organization: lead.organization,
    name: lead.name || "",
    phone: lead.phone || "",
    email: lead.email || undefined,
    city: lead.city || "",
    projectType: lead.project_type || lead.projectType || undefined,
    propertySize: lead.property_size || lead.propertySize || undefined,
    budget: parseBudgetToNumber(lead.budget),
    startDate: lead.start_date || lead.startDate || undefined,
    notes: lead.remarks || lead.notes || undefined,
    status: lead.status || "lead",
    final_budget: parseBudgetToNumber(lead.final_budget),
    createdAt: createdRaw ? String(createdRaw).split("T")[0] : undefined,
    projectsCount: 0,
  };
}

const mockClients = [
  {
    id: "1",
    name: "Rohan Sharma",
    phone: "+91 98765 43210",
    email: "rohan@email.com",
    city: "Mumbai",
    status: "lead",
    createdAt: "2025-12-20",
    projectsCount: 1,
    budget: 1500000,
    startDate: "2026-01-15",
    projectType: "interior",
    propertySize: "1,200 sq.ft",
    notes: "Interested in modern minimalist design",
  },
  {
    id: "2",
    name: "Priya Patel",
    phone: "+91 87654 32109",
    email: "priya.patel@email.com",
    city: "Pune",
    status: "active",
    createdAt: "2025-12-15",
    projectsCount: 2,
    budget: 2500000,
    startDate: "2025-12-28",
    projectType: "interior",
    propertySize: "1,800 sq.ft",
  },
  {
    id: "3",
    name: "Amit Kumar",
    phone: "+91 76543 21098",
    city: "Delhi",
    status: "lead",
    createdAt: "2025-12-22",
    projectsCount: 0,
    budget: 800000,
    startDate: "2026-02-01",
    projectType: "construction",
    propertySize: "2,500 sq.ft",
  },
  {
    id: "4",
    name: "Sneha Reddy",
    phone: "+91 65432 10987",
    email: "sneha.r@email.com",
    city: "Hyderabad",
    status: "completed",
    createdAt: "2025-11-10",
    projectsCount: 1,
    budget: 1200000,
    startDate: "2025-11-15",
    projectType: "interior",
    propertySize: "950 sq.ft",
  },
  {
    id: "5",
    name: "Vikram Singh",
    phone: "+91 54321 09876",
    email: "vikram.s@email.com",
    city: "Bangalore",
    status: "active",
    createdAt: "2025-12-10",
    projectsCount: 3,
    budget: 3500000,
    startDate: "2025-12-20",
    projectType: "construction",
    propertySize: "3,200 sq.ft",
  },
  {
    id: "6",
    name: "Meera Nair",
    phone: "+91 43210 98765",
    city: "Chennai",
    status: "lead",
    createdAt: "2025-12-25",
    projectsCount: 0,
    budget: 950000,
    startDate: "2026-03-01",
    projectType: "interior",
    propertySize: "1,100 sq.ft",
  },
];

export function ClientsDashboard({ onSelectClient, selectedClientId }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateDialogMounted, setIsCreateDialogMounted] = useState(false);
  const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false);
  const closeResetTimerRef = useRef(null);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [createLeadError, setCreateLeadError] = useState("");
  const [clients, setClients] = useState(
    JSON.parse(localStorage.getItem("clients")) || [],
  );
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState("");
  const [isSelectingLeadId, setIsSelectingLeadId] = useState(null);
  const [recentActivities, setRecentActivities] = useState(
    JSON.parse(localStorage.getItem("recentActivities")) || [],
  );
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    city: "",
    projectType: "",
    propertySize: "",
    budgetRange: "",
    tentativeStartDate: "",
    notes: "",
  });
  // ── Phone validation state (mirrors AddVendor pattern) ──
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [isProjectTypeSelectOpen, setIsProjectTypeSelectOpen] = useState(false);
  const [isBudgetSelectOpen, setIsBudgetSelectOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const organizationKey = localStorage.getItem("organization");

  useEffect(() => {
    if (closeResetTimerRef.current) {
      clearTimeout(closeResetTimerRef.current);
      closeResetTimerRef.current = null;
    }

    if (isCreateDialogOpen) {
      setIsCreateDialogMounted(true);
      const enterTimer = setTimeout(() => setIsCreateDialogVisible(true), 10);
      return () => clearTimeout(enterTimer);
    }

    setIsCreateDialogVisible(false);
    const exitTimer = setTimeout(() => setIsCreateDialogMounted(false), 250);
    return () => clearTimeout(exitTimer);
  }, [isCreateDialogOpen]);

  useEffect(() => {
    return () => {
      if (closeResetTimerRef.current) {
        clearTimeout(closeResetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    if (!orgId) return;

    let cancelled = false;
    (async () => {
      setClientsError("");
      if (clients.length === 0) {
        setIsLoadingClients(true);
      }
      try {
        const res = await LeadAPI.listLeads(orgId);
        const list = Array.isArray(res) ? res : res?.data || res?.results || [];
        const normalized = (list || [])
          .map(normalizeLeadToClient)
          .filter(Boolean)
          .filter((c) => c.id);
        if (!cancelled) {
          setClients(normalized);
          localStorage.setItem("clients", JSON.stringify(normalized));
        }
      } catch (e) {
        if (!cancelled) {
          setClientsError(e?.message || "Failed to load leads");
          setClients(mockClients);
        }
      } finally {
        if (!cancelled) setIsLoadingClients(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationKey]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    if (!orgId) return;

    let cancelled = false;
    (async () => {
      if (recentActivities.length === 0) {
        setIsLoadingActivities(true);
      }
      try {
        const res = await LeadAPI.getRecentActivities(orgId);
        const data = res?.data || res || [];
        if (!cancelled) {
          setRecentActivities(Array.isArray(data) ? data : []);
          localStorage.setItem("recentActivities", JSON.stringify(data));
        }
      } catch (e) {
        console.error("Failed to load recent activities:", e);
      } finally {
        if (!cancelled) setIsLoadingActivities(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationKey]);
  // ── Strips non-digits, caps at 10, validates length in real time ──
  const handlePhoneInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setPhoneNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Contact number must be exactly 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const q = searchQuery.trim().toLowerCase();
  // const filteredClients = clients.filter((client) => {
  //   if (!q) return true;
  //   return (
  //     String(client?.name || "")
  //       .toLowerCase()
  //       .includes(q) ||
  //     String(client?.city || "")
  //       .toLowerCase()
  //       .includes(q) ||
  //     String(client?.phone || "")
  //       .toLowerCase()
  //       .includes(q) ||
  //     String(client?.email || "")
  //       .toLowerCase()
  //       .includes(q)
  //   );
  // });
  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      !q ||
      String(client?.name || "")
        .toLowerCase()
        .includes(q) ||
      String(client?.city || "")
        .toLowerCase()
        .includes(q) ||
      String(client?.phone || "")
        .toLowerCase()
        .includes(q) ||
      String(client?.email || "")
        .toLowerCase()
        .includes(q);
    const matchesFilter =
      activeFilter === "all" ||
      String(client?.status || "").toLowerCase() === activeFilter;
    return matchesSearch && matchesFilter;
  });

  const totalBudget = clients.reduce((sum, c) => sum + (c.budget || 0), 0);
  const activeBudget = clients
    .filter((c) => c.status === "completed")
    .reduce((sum, c) => sum + (c.final_budget || 0), 0);
  const leadBudget = clients
    .filter((c) => c.status === "lead")
    .reduce((sum, c) => sum + (c.budget || 0), 0);

  const cityCounts = clients.reduce((acc, c) => {
    const city = (c.city || "").trim();
    if (!city) return acc;
    acc[city] = (acc[city] || 0) + 1;
    return acc;
  }, {});
  const cityData = Object.entries(cityCounts)
    .map(([city, count]) => ({ city, count }))
    .sort((a, b) => b.count - a.count)
    .map((x) => ({
      ...x,
      percentage: clients.length
        ? Math.round((x.count / clients.length) * 100)
        : 0,
    }));

  const getStatusBadge = (status) => {
    const styles = {
      lead: "bg-orange-100 text-orange-700",
      active: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700",
      quotation_active: "bg-purple-100 text-purple-700",
    };
    const labels = {
      lead: "Lead",
      active: "Active",
      completed: "Done",
      quotation_active: "Quotation",
    };
    return (
      <span
        className={`px-1.5 py-0.5 rounded text-xs font-medium ${styles[status]}`}
      >
        {labels[status]}
      </span>
    );
  };

  const resetCreateLeadForm = () => {
    setIsProjectTypeSelectOpen(false);
    setIsBudgetSelectOpen(false);
    setCreateLeadError("");
    setPhoneNumber("");
    setPhoneError("");
    setNewClient({
      name: "",
      email: "",
      city: "",
      projectType: "",
      propertySize: "",
      budgetRange: "",
      tentativeStartDate: "",
      notes: "",
    });
  };

  const openCreateLeadDialog = () => {
    if (closeResetTimerRef.current) {
      clearTimeout(closeResetTimerRef.current);
      closeResetTimerRef.current = null;
    }
    setIsCreateDialogOpen(true);
  };

  const closeCreateLeadDialog = ({ resetForm = true } = {}) => {
    setIsCreateDialogOpen(false);
    if (!resetForm) return;
    closeResetTimerRef.current = setTimeout(() => {
      resetCreateLeadForm();
      closeResetTimerRef.current = null;
    }, 250);
  };

  const handleCreateClient = async () => {
    setCreateLeadError("");

    // ── 10-digit guard (runs before any other validation) ──
    if (phoneNumber.length !== 10) {
      setPhoneError("Contact number must be exactly 10 digits");
      return;
    }
    setPhoneError("");

    const orgId = LeadAPI.orgId();
    if (!orgId) {
      setCreateLeadError(
        "Organization not found. Please re-login or select an organization.",
      );
      return;
    }

    const budgetAmount = parseBudgetRangeToBudgetAmount(newClient.budgetRange);
    if (!newClient.tentativeStartDate) {
      setCreateLeadError("Please fill the tentative start date");
      return;
    }

    const fullPhone = "+91" + phoneNumber; // attach prefix only for API

    const payload = {
      name: newClient.name,
      phone: fullPhone,
      city: newClient.city,
      project_type: newClient.projectType,
      status: "lead",
      organization: orgId,
      ...(newClient.email ? { email: newClient.email } : {}),
      ...(newClient.propertySize
        ? { property_size: newClient.propertySize }
        : {}),
      ...(budgetAmount != null ? { budget: budgetAmount } : {}),
      ...(newClient.tentativeStartDate
        ? { start_date: newClient.tentativeStartDate }
        : {}),
      ...(newClient.notes ? { remarks: newClient.notes } : {}),
    };

    setIsCreatingLead(true);
    try {
      const res = await LeadAPI.createLead(orgId, payload);
      const created = res?.data || res?.result || res;

      const newClientData = {
        id: String(created?.id || Date.now()),
        name: created?.name || newClient.name,
        phone: created?.phone || fullPhone,
        email: created?.email || newClient.email || undefined,
        city: created?.city || newClient.city,
        status: created?.status || "lead",
        createdAt: (
          created?.created_at ||
          created?.createdAt ||
          new Date().toISOString()
        ).split("T")[0],
        projectsCount: 0,
        budget: created?.budget ?? budgetAmount ?? undefined,
        startDate:
          created?.start_date || newClient.tentativeStartDate || undefined,
        projectType:
          created?.project_type || newClient.projectType || undefined,
        propertySize:
          created?.property_size || newClient.propertySize || undefined,
        notes: created?.remarks || newClient.notes || undefined,
      };

      closeCreateLeadDialog();
      setClients((prev) => {
        const next = Array.isArray(prev) ? prev : [];
        const deduped = next.filter(
          (c) => String(c.id) !== String(newClientData.id),
        );
        return [newClientData, ...deduped];
      });
      onSelectClient(newClientData);
    } catch (e) {
      setCreateLeadError(
        e?.message || "Failed to create lead. Please try again.",
      );
    } finally {
      setIsCreatingLead(false);
    }
  };

  const handleSelectClient = async (client) => {
    const orgId = LeadAPI.orgId();
    if (!orgId) {
      onSelectClient(client);
      return;
    }

    setIsSelectingLeadId(String(client.id));
    try {
      const res = await LeadAPI.getLead(orgId, client.id);
      const lead = res?.data || res;
      const mapped = normalizeLeadToClient(lead);
      onSelectClient(mapped || client);
    } catch (_) {
      onSelectClient(client);
    } finally {
      setIsSelectingLeadId(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (_) {
      return dateStr;
    }
  };

  const projectTypeOptions = [
    { value: "interior", label: "Interior" },
    { value: "construction", label: "Construction" },
  ];

  const budgetOptions = [
    { value: "5-10", label: "₹5-10 Lakhs" },
    { value: "10-15", label: "₹10-15 Lakhs" },
    { value: "15-25", label: "₹15-25 Lakhs" },
    { value: "25-50", label: "₹25-50 Lakhs" },
    { value: "50+", label: "₹50+ Lakhs" },
  ];

  // The submit button should also be disabled when phoneNumber isn't 10 digits
  const isPhoneValid = phoneNumber.length === 10;

  return (
    <LoadingScreen loading={isLoadingClients || isLoadingActivities}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Lead Management
            </h1>
            <p className="text-sm text-muted-bg">
              Overview of clients, budgets & schedules
            </p>
          </div>
          <button
            onClick={openCreateLeadDialog}
            className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium text-white bg-sm-primary rounded-md hover:bg-sm-hover transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Lead
          </button>
        </div>

        {/* Create Lead Right Sidebar */}
        {isCreateDialogMounted && (
          <div
            className={`fixed inset-0 z-50 flex items-stretch justify-end transition-colors duration-300 ${
              isCreateDialogVisible ? "bg-black/50" : "bg-black/0"
            }`}
            onClick={() => closeCreateLeadDialog()}
          >
            <div
              className={`bg-white shadow-2xl w-full max-w-xl overflow-hidden flex flex-col h-full rounded-l-xl transform transition-transform duration-300 ease-out ${
                isCreateDialogVisible ? "translate-x-0" : "translate-x-full"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-row items-center justify-between px-4 h-16 py-2 border-b border-gray-100 relative shrink-0">
                <div />
                <h3 className="text-xl font-medium text-[#232323] text-center">
                  Create New Lead
                </h3>
                <button
                  onClick={() => closeCreateLeadDialog()}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 min-h-0 p-6 overflow-y-auto">
                {createLeadError ? (
                  <div className="mb-6 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-sm text-red-700">
                    {createLeadError}
                  </div>
                ) : null}

                {/* Client Details Section */}
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-[#232323] mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Client Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Name *
                      </label>
                      <input
                        placeholder="Client name"
                        className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newClient.name}
                        onChange={(e) =>
                          setNewClient({ ...newClient, name: e.target.value })
                        }
                      />
                    </div>

                    {/* ── Phone with +91 prefix & 10-digit validation ── */}
                    <div>
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Phone *
                      </label>
                      <div
                        className={`flex items-center px-4 py-2 text-sm rounded-xl bg-[#F3F4F6] transition-colors border ${
                          phoneError
                            ? "border-red-500"
                            : "border-transparent focus-within:ring-2 focus-within:ring-purple-500"
                        }`}
                      >
                        <span className="mr-1.5 text-gray-500 select-none shrink-0">
                          +91
                        </span>
                        <input
                          type="text"
                          maxLength={10}
                          value={phoneNumber}
                          onInput={handlePhoneInput}
                          placeholder="9999999999"
                          className="w-full bg-transparent outline-none placeholder:text-gray-400"
                        />
                      </div>
                      {phoneError && (
                        <p className="text-xs text-red-500 font-medium">
                          {phoneError}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Email (optional)
                      </label>
                      <input
                        type="email"
                        placeholder="Email address"
                        className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newClient.email}
                        onChange={(e) =>
                          setNewClient({ ...newClient, email: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        City *
                      </label>
                      <input
                        placeholder="City"
                        className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newClient.city}
                        onChange={(e) =>
                          setNewClient({ ...newClient, city: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Project Requirement Details Section */}
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-[#232323] mb-4 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Project Requirement Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Project Type *
                      </label>
                      <button
                        onClick={() =>
                          setIsProjectTypeSelectOpen(!isProjectTypeSelectOpen)
                        }
                        className="w-full px-4 py-2 text-sm flex items-center justify-between bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <span
                          className={
                            !newClient.projectType ? "text-gray-400" : ""
                          }
                        >
                          {projectTypeOptions.find(
                            (opt) => opt.value === newClient.projectType,
                          )?.label || "Select type"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                      {isProjectTypeSelectOpen && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-52 overflow-y-auto">
                          {projectTypeOptions.map((opt) => (
                            <button
                              key={opt.value}
                              className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                                newClient.projectType === opt.value
                                  ? "text-sm-primary font-medium bg-sm-primary/5"
                                  : "text-gray-700"
                              }`}
                              onClick={() => {
                                setNewClient({
                                  ...newClient,
                                  projectType: opt.value,
                                });
                                setIsProjectTypeSelectOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Property Size
                      </label>
                      <input
                        placeholder="e.g., 1,200 sq.ft"
                        className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newClient.propertySize}
                        onChange={(e) =>
                          setNewClient({
                            ...newClient,
                            propertySize: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Budget Range
                      </label>
                      <button
                        onClick={() =>
                          setIsBudgetSelectOpen(!isBudgetSelectOpen)
                        }
                        className="w-full px-4 py-2 text-sm flex items-center justify-between bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <span
                          className={
                            !newClient.budgetRange ? "text-gray-400" : ""
                          }
                        >
                          {budgetOptions.find(
                            (opt) => opt.value === newClient.budgetRange,
                          )?.label || "Select range"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                      {isBudgetSelectOpen && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-52 overflow-y-auto">
                          {budgetOptions.map((opt) => (
                            <button
                              key={opt.value}
                              className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                                newClient.budgetRange === opt.value
                                  ? "text-sm-primary font-medium bg-sm-primary/5"
                                  : "text-gray-700"
                              }`}
                              onClick={() => {
                                setNewClient({
                                  ...newClient,
                                  budgetRange: opt.value,
                                });
                                setIsBudgetSelectOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#232323] mb-2">
                        Tentative Start Date *
                      </label>
                      <input
                        type="date"
                        className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newClient.tentativeStartDate}
                        onChange={(e) =>
                          setNewClient({
                            ...newClient,
                            tentativeStartDate: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#232323] mb-2">
                      Notes / Initial Requirement Summary
                    </label>
                    <textarea
                      placeholder="Any initial requirements or notes about the project..."
                      className="w-full min-h-[150px] p-3 text-sm bg-[#F3F4F6] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                      value={newClient.notes}
                      onChange={(e) =>
                        setNewClient({ ...newClient, notes: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 border-t flex flex-col items-center justify-center border-gray-100 shrink-0 bg-white">
                <button
                  className={`min-w-[240px] text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
                    !newClient.name ||
                    !isPhoneValid ||
                    !newClient.city ||
                    !newClient.projectType ||
                    !newClient.tentativeStartDate ||
                    isCreatingLead
                      ? "bg-[#3B0069] cursor-not-allowed"
                      : "bg-[#3B0069] hover:bg-[#3B0069D9]"
                  }`}
                  onClick={handleCreateClient}
                  style={{
                    alignSelf: "center",
                    opacity:
                      !newClient.name ||
                      !isPhoneValid ||
                      !newClient.city ||
                      !newClient.projectType ||
                      !newClient.tentativeStartDate ||
                      isCreatingLead
                        ? 0.4
                        : 1,
                  }}
                  disabled={
                    !newClient.name ||
                    !isPhoneValid ||
                    !newClient.city ||
                    !newClient.projectType ||
                    !newClient.tentativeStartDate ||
                    isCreatingLead
                  }
                >
                  {isCreatingLead ? "Creating..." : "Create Lead"}
                  <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
                    <Check size={18} />
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Cards Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-sm-primary/10">
                <IndianRupee className="w-4 h-4 text-sm-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(totalBudget)}
                </p>
                <p className="text-xs text-gray-500">Total Pipeline</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-blue-50">
                <TrendingUp className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(activeBudget)}
                </p>
                <p className="text-xs text-gray-500">
                  Converted Projects Value
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-orange-50">
                <Users className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(leadBudget)}
                </p>
                <p className="text-xs text-gray-500">Leads Value</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-green-50">
                <Building2 className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {clients.filter((c) => c.status === "completed").length}
                </p>
                <p className="text-xs text-gray-500">Total Projects</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="flex-1 grid grid-cols-5 lg:grid-cols-5 gap-4 min-h-0">
          {/* Clients List */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 flex flex-col overflow-hidden">
            <div className="p-3 pb-2 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Clients
                </h3>
                <div className="flex gap-1">
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                    {clients.filter((c) => c.status === "lead").length} Leads
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {clients.filter((c) => c.status === "active").length} Active
                  </span>
                </div>
              </div>
              <div className="relative mt-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  placeholder="Search clients..."
                  className="w-full pl-8 h-7 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                {[
                  { label: "All", value: "all" },
                  { label: "Lead", value: "lead" },
                  { label: "Active", value: "active" },
                  { label: "Quotation", value: "quotation_active" },
                  { label: "Done", value: "completed" },
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setActiveFilter(f.value)}
                    className={`px-3 py-0.5 text-sm font-medium rounded-full transition-colors ${
                      activeFilter === f.value
                        ? "bg-sm-primary text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 pt-2 flex-1 overflow-auto">
              <div className="space-y-1.5">
                {clientsError ? (
                  <div className="p-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md">
                    {clientsError}
                  </div>
                ) : null}

                {isLoadingClients ? (
                  <div className="p-2 text-xs text-gray-500">
                    Loading leads...
                  </div>
                ) : null}

                {!isLoadingClients && filteredClients.length === 0 ? (
                  <div className="p-2 text-xs text-gray-500">
                    No leads found.
                  </div>
                ) : null}

                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    disabled={String(isSelectingLeadId) === String(client.id)}
                    className={`w-full text-left p-2 rounded-md border transition-all duration-150 ${
                      selectedClientId === client.id
                        ? "bg-sm-primary/5 border-sm-primary"
                        : "bg-white border-gray-100 hover:border-sm-primary/40"
                    } ${
                      String(isSelectingLeadId) === String(client.id)
                        ? "opacity-70 cursor-wait"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <h3 className="font-medium text-sm text-gray-900">
                        {client.name}
                      </h3>
                      {getStatusBadge(client.status)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" />
                        {client.city}
                      </span>
                      {client.budget && (
                        <span className="text-sm-primary font-medium">
                          Rs. {formatCurrency(client.budget)}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Timeline & Location */}
          <div className="lg:col-span-3 flex flex-col gap-4 min-h-0">
            {/* Recent Activities View */}
            <div className="bg-white rounded-lg border border-gray-200 flex-1 flex flex-col overflow-hidden">
              <div className="p-3 pb-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                    <Activity className="w-4 h-4 text-sm-primary" />
                    Recent Activities
                  </h3>
                </div>
              </div>
              <div className="p-3 pt-0 flex-1 overflow-auto">
                <div className="space-y-2 mt-3">
                  {isLoadingActivities ? (
                    <div className="p-4 text-center text-xs text-gray-500">
                      Loading activities...
                    </div>
                  ) : recentActivities.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-500 transition-opacity">
                      No recent activities.
                    </div>
                  ) : (
                    recentActivities.map((activity, idx) => (
                      <div
                        key={activity.lead_id + "-" + idx}
                        className="flex items-start gap-3 p-2.5 rounded-md bg-gray-50 border border-gray-100 hover:bg-white transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-sm-primary/10 text-sm-primary">
                              {activity.category}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {formatDateTime(activity.updated_at)}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 leading-tight">
                            {activity.lead_name}
                          </p>
                          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                            {activity.description}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Location Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 pb-2 border-b border-gray-100">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <MapPin className="w-4 h-4 text-sm-primary" />
                  Client Locations
                </h3>
              </div>
              <div className="p-3 pt-0">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
                  {cityData.map((city) => (
                    <div
                      key={city.city}
                      className="p-2 rounded-md bg-gray-50 border border-gray-100 text-center"
                    >
                      <p className="text-sm font-bold text-sm-primary">
                        {city.count}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {city.city}
                      </p>
                      <div className="mt-1 h-1 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-sm-primary rounded-full"
                          style={{ width: `${city.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadingScreen>
  );
}
